<script setup lang="ts">
import NamaIcon from "@/components/icons/NamaIcon.vue";
import QuickFilterUtils from "@/utils/common/QuickFilterUtils.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import {onMounted, onUnmounted, ref, useTemplateRef} from "vue";
import {QMenu} from "quasar";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import ColorUtils from "@/utils/common/ColorUtils.ts";
import ListViewQuickFilterGroupVal = namaTypes.ListViewQuickFilterGroupVal;
import ListViewQuickFilterButtonVal = namaTypes.ListViewQuickFilterButtonVal;

const {compactMode, isUltra} = storeToRefs(useNamaFlagsStore());
const props = defineProps<{ presenter: ListPresenter | SearcherViewPresenter }>()

const util = new QuickFilterUtils(props.presenter);
const qMenuModels = ref<Record<string, boolean>>({})
const qMenuRefs = useTemplateRef<QMenu[]>("qMenuRefs")

function onDocumentClick(e: MouseEvent) {
  const target = e.target as HTMLElement;

  const isInsideMenu = qMenuRefs.value?.some(m => m?.contentEl?.contains(target));

  if (!isInsideMenu)
    Object.keys(qMenuModels.value).forEach(k => qMenuModels.value[k] = false);
}

onMounted(() => document.addEventListener('click', onDocumentClick, true))
onUnmounted(() => document.removeEventListener('click', onDocumentClick, true))

function isMenuOpen(group: ListViewQuickFilterGroupVal): boolean {
  return qMenuModels.value[util.getQuickFilterTitle(group) ?? ''] ?? false;
}

function setMenuOpen(group: ListViewQuickFilterGroupVal, open: boolean) {
  qMenuModels.value[util.getQuickFilterTitle(group) ?? ''] = open;
}

function getInitialWidth(group: ListViewQuickFilterGroupVal): number {
  let initialWidth = 150;
  const titleLength = util.getQuickFilterTitle(group)?.length ?? 0
  if (titleLength < 0)
    return initialWidth;
  else
    initialWidth += titleLength * 2
  return initialWidth;
}

function onDropdownToggle(button: ListViewQuickFilterButtonVal) {
  util.toggleSelectedQuickFilterButton(button);
}

</script>

<template>
  <div v-if="presenter.quickFilterGroup.value && !!presenter.quickFilterGroup.value.length"
       class="tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-2.5 compact:tw-gap-x-1.5 compact:tw-gap-y-2 ultra:tw-gap-x-1 ultra:tw-gap-y-1.5 tw-max-h-[20vh] tw-shrink-0" data-scrollbar-reveal>
    <template v-for="quickFilterGroup in presenter.quickFilterGroup.value">
      <template v-if="quickFilterGroup.buttons?.length">
        <div
          v-if="util.isDropdown(quickFilterGroup)"
          :style="{minWidth:getInitialWidth(quickFilterGroup) + 'px'}"
          class="tw-flex mobile:tw-grow tw-flex-wrap tw-items-center
             tw-border tw-border-black/10 dark:tw-border-white/20 tw-rounded-md  tw-w-max tw-gap-1 tw-relative">
            <span v-text="util.getQuickFilterTitle(quickFilterGroup)"
                  class="tw-absolute tw-left-4 tw-top-0 tw-px-1 -tw-translate-y-1/2 tw-rounded-md tw-text-black tw-text-[11px] dark:tw-text-white tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark"/>
          <div class="tw-flex tw-flex-wrap tw-gap-1 tw-items-center tw-p-1 ultra:tw-p-0.5 tw-justify-between tw-w-full">
            <div class="tw-flex items-center tw-flex-wrap tw-gap-1">
            <template v-for="button in util.getModelValueForDropdown(quickFilterGroup).value">
              <q-chip
                removable
                @remove="util.toggleSelectedQuickFilterButton(button)"
                :style="{backgroundColor:util.getQuickFilterButtonBG(button).value}"
                :text-color="ColorUtils.isDarkColor(util.getQuickFilterButtonBG(button).value)?'white':'black'"
                size="sm">
                <div class="tw-flex tw-items-center tw-gap-1 tw-justify-center">
                  <span v-if="quickFilterGroup.showCount" class="tw-text-xs tw-font-bold tw-whitespace-nowrap">
                    {{ NamaCommon.formatToK(button.count) }}
                    <span class="tw-text-[10px] tw-px-1 tw-py-[1px] tw-rounded tw-bg-black/20 dark:tw-bg-white/20">{{ Number(util.getQuickFilterPercentage(quickFilterGroup, button)).toFixed(1) }}%</span>
                  </span>
                  <span class="tw-text-xs" v-html="util.getQuickFilterColumnValueButtonText(button)"/>
                </div>
              </q-chip>
            </template>
            </div>
            <q-btn flat dense icon="add" size="sm">
              <q-menu
                ref="qMenuRefs"
                persistent
                :model-value="isMenuOpen(quickFilterGroup)"
                @update:model-value="setMenuOpen(quickFilterGroup, $event)"
                anchor="bottom start"
                self="top start"
                class="!tw-max-h-[30vh] dark:tw-bg-namaBackground-bright-dark">
                <q-list dense>
                  <q-item
                    v-for="button in quickFilterGroup.buttons"
                    clickable
                    @click="onDropdownToggle(button)">
                    <q-item-section side>
                      <q-checkbox
                        :model-value="util.isQuickFilterButtonSelected(button).value"
                        @click.stop.prevent="onDropdownToggle(button)"
                        dense size="sm"/>
                    </q-item-section>
                    <q-item-section side v-if="quickFilterGroup.showCount">
                      <span class="tw-text-xs tw-font-bold tw-whitespace-nowrap">
                        {{ NamaCommon.formatToK(button.count) }}
                        <span class="tw-text-[11px] tw-px-1 tw-py-[1px] tw-rounded tw-bg-black/10 dark:tw-bg-white/10">{{ Number(util.getQuickFilterPercentage(quickFilterGroup, button)).toFixed(1) }}%</span>
                      </span>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label v-html="util.getQuickFilterColumnValueButtonText(button)"/>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>

        <div
          v-else
          :style="{minWidth:getInitialWidth(quickFilterGroup) + 'px'}"
          class="tw-flex mobile:tw-grow tw-flex-wrap tw-items-center tw-border tw-border-black/10 dark:tw-border-white/20 tw-rounded-md tw-pt-2 compact:tw-pt-1.5 ultra:tw-pt-1 tw-p-1.5 compact:tw-p-1 ultra:tw-p-0.5 tw-w-max tw-gap-1 tw-relative">
          <span v-text="util.getQuickFilterTitle(quickFilterGroup)"
                class="tw-absolute tw-left-4 tw-top-0 tw-px-1 -tw-translate-y-1/2 tw-rounded-md tw-text-black tw-text-[11px] dark:tw-text-white tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark"/>
        <template v-for="button in quickFilterGroup.buttons">
          <div :title="quickFilterGroup.showCount?util.getQuickFilterPercentage(quickFilterGroup,button)+'%':undefined"
               @click="util.toggleSelectedQuickFilterButton(button)"
               class="tw-relative tw-grow tw-overflow-hidden tw-rounded-md tw-select-none tw-bg-gray-600/40 hover:tw-bg-gray-600/50 tw-px-1 ultra:tw-px-0.5 tw-py-0.5 ultra:tw-py-0 tw-flex tw-items-center tw-gap-1 tw-shadow-md tw-cursor-pointer">
            <NamaIcon
              v-bind="{...util.quickFilterButtonToNamaIconProps(button)}"
              class="tw-size-3 active-white tw-z-[1]"/>
            <span
              :class="{'!tw-opacity-100':util.isQuickFilterButtonSelected(button).value}"
              :style="{backgroundColor:util.getQuickFilterButtonBG(button).value}"
              class="tw-absolute tw-inset-0 tw-opacity-[0.24] -tw-z-0 dark:tw-opacity-[0.08] tw-w-full tw-h-full"/>
            <span
              v-html="util.getQuickFilterColumnValueButtonText(button)"
              class=" tw-text-white dark:tw-text-white/90 tw-text-[12px] ultra:tw-text-[11px] tw-z-[2]"/>
            <q-space/>
            <svg v-if="quickFilterGroup.showCount" class="tw-size-6 ultra:tw-size-5 tw-z-[2]" viewBox="0 0 36 36">
              <circle
                cx="18" cy="18" r="15.915"
                fill="none"
                stroke="rgba(255, 255, 255, 0.2)"
                stroke-width="2"/>
              <circle
                :stroke-dashoffset="100 - Number(util.getQuickFilterPercentage(quickFilterGroup,button))"
                cx="18" cy="18" r="15.915"
                fill="none"
                stroke="white"
                stroke-width="2"
                stroke-dasharray="100"
                stroke-linecap="round"
                transform="rotate(-90 18 18)"/>
              <text v-text="NamaCommon.formatToK(button.count)" x="18" y="22" class="tw-fill-white tw-text-[12px] tw-font-bold"
                    text-anchor="middle"/>
            </svg>
          </div>
        </template>
      </div>
      </template>
    </template>
  </div>
</template>
